import { defineElement, html, TemplateElement } from '@webtides/element-js';

/**
 * @property {string} templateId
 * @property {boolean} needsCookieConsent
 * @property {string} cookieConsentGroup
 * @property {'iframe-resizer' | 'logismata' | 'none'} iframeEmbedType
 * @property {boolean} iframeEmbedLog
 */
export default class HtmlComponent extends TemplateElement {
    constructor() {
        super({ shadowRender: true });
    }
    properties() {
        return {
            templateId: '',
            needsCookieConsent: false,
            cookieConsentGroup: 'C0001',
            // TODO(migrate): the intended future default is 'none'. Existing JCR content
            // has no `iframeEmbedType` value and relies on the implicit iframe-resizer
            // behaviour, so we keep 'iframe-resizer' as the fallback until that content
            // has been migrated to an explicit value.
            iframeEmbedType: 'iframe-resizer',
            consented: false,
            iframeEmbedLog: false,
        };
    }
    connected() {
        if (this.needsCookieConsent) {
            this.updateCookieConsent();
        } else {
            this.showContent();
        }
    }

    updateCookieConsent() {
        if (window.OptanonActiveGroups && window.OptanonActiveGroups.includes(this.cookieConsentGroup)) {
            this.showContent();
        } else {
            this.showConsentBanner();
        }
    }

    async showContent() {
        this.consented = true;
        const template = document.getElementById(this.templateId);
        if (!template) return;
        this.blockUnconsentedElements(template.content);

        // Bind the Logismata listener before the iframe is inserted below, so the
        // calculator's initial postMessage (sent as soon as it loads) isn't missed.
        if (this.iframeEmbedType === 'logismata') this.bindLogismata();

        const scripts = [...template.content.querySelectorAll('script')];
        scripts.forEach((s) => s.remove());
        this.appendChild(template.content);

        for (const original of scripts) {
            if (original.type === 'text/plain') {
                const placeholder = this.cloneScript(original);
                placeholder.textContent = original.textContent;
                this.appendChild(placeholder);
                continue;
            }
            await this.activateScript(original);
        }
        await this.processOptanonScripts();
        this.applyIframeEmbedding();
    }

    applyIframeEmbedding() {
        switch (this.iframeEmbedType) {
            case 'logismata':
                // Bound eagerly in showContent(), before the iframe loads.
                break;
            case 'none':
                break;
            case 'iframe-resizer':
            default:
                this.resizeIframes();
        }
    }

    resizeIframes() {
        const iframes = [...this.querySelectorAll('iframe')].filter((iframe) => !iframe.iFrameResizer);
        if (!iframes.length) return;
        import('iframe-resizer/js/iframeResizer').then(() => {
            iframes.forEach((iframe) => {
                const init = () => {
                    if (!iframe.iFrameResizer) iFrameResize({ log: this.iframeEmbedLog }, iframe);
                };
                // Defer until the iframe has navigated; otherwise iframe-resizer's
                // initial postMessage targets an origin the contentWindow doesn't yet have.
                iframe.addEventListener('load', init, { once: true });
            });
        });
    }

    bindLogismata() {
        import('../../../../js/vendor/quickwinbinding.js').then(({ bindQuickWin }) => {
            // `bindQuickWin` installs a single page-level postMessage listener that
            // resolves the source iframe dynamically. It must run only once per page
            // even when several Logismata calculators are present, and is bound before
            // the iframes load so their initial height messages aren't missed.
            if (window.__mbQuickWinBound) return;
            window.__mbQuickWinBound = true;
            // TODO: lock down to the production calculator origin, i.e.
            // bindQuickWin(['https://saasquickwin.logismata.ch'], …), once confirmed.
            // Passing undefined origins authorises any origin for now.
            // `iframeEmbedLog` gates the vendored script's verbose logs (off by default),
            // fed from the site-level enableIframeResizerLogging flag via the FTL.
            bindQuickWin(undefined, this.iframeEmbedLog);
        });
    }

    activateScript(original, { stripTextPlainType = false } = {}) {
        return new Promise((resolve) => {
            const script = this.cloneScript(original, { stripTextPlainType });
            script.textContent = original.textContent;

            if (!original.src) {
                this.appendChild(script);
                resolve();
                return;
            }
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener(
                'error',
                () => {
                    console.warn(`[mb-html-component] Failed to load script: ${original.src}`);
                    resolve();
                },
                { once: true },
            );
            this.appendChild(script);
        });
    }

    cloneScript(original, { stripTextPlainType = false } = {}) {
        const script = document.createElement('script');

        for (const attr of original.attributes) {
            if (stripTextPlainType && attr.name === 'type' && attr.value === 'text/plain') continue;
            script.setAttribute(attr.name, attr.value);
        }

        return script;
    }

    async processOptanonScripts() {
        const activeGroups = window.OptanonActiveGroups || '';

        for (const script of this.querySelectorAll('script[type="text/plain"]')) {
            const group = this.getOptanonGroup(script);
            if (!group || !activeGroups.includes(group)) continue;
            await this.activateScript(script, { stripTextPlainType: true });
            script.remove();
        }

        this.querySelectorAll('[data-src]').forEach((element) => {
            const group = this.getOptanonGroup(element);
            if (!group || !activeGroups.includes(group)) return;
            element.src = element.dataset.src;
            element.removeAttribute('data-src');
        });
    }

    // TODO: Remove getOptanonGroup, blockUnconsentedElements and processOptanonScripts once all
    // legacy CMS iframe/htmlCode content has been migrated to use the cookieConsentGroup dialog
    // field and the mb-cookie-consent-banner wrapper with needs-cookie-consent="true".
    getOptanonGroup(element) {
        const optanonClass = [...element.classList].find((c) => c.startsWith('optanon-category-'));
        return optanonClass ? optanonClass.replace('optanon-category-', '') : null;
    }

    blockUnconsentedElements(fragment) {
        const activeGroups = window.OptanonActiveGroups || '';
        fragment.querySelectorAll('[src]').forEach((element) => {
            const group = this.getOptanonGroup(element);
            if (!group || activeGroups.includes(group)) return;
            element.dataset.src = element.getAttribute('src');
            element.removeAttribute('src');
        });
    }

    showConsentBanner() {
        this.consented = false;

        const slot = this.$refs.defaultSlot;
        if (slot) {
            const assignedNodes = slot.assignedNodes({ flatten: true });

            assignedNodes.forEach((node) => {
                node.remove();
            });
        }
    }

    events() {
        return {
            window: {
                OneTrustGroupsUpdated: () => {
                    this.updateCookieConsent();
                },
            },
            document: {
                'optanon-ready': () => {
                    this.updateCookieConsent();
                },
            },
        };
    }

    template() {
        if (!this.consented) return null;
        return html` <slot ref="defaultSlot"></slot> `;
    }
}

defineElement('mb-html-component', HtmlComponent);
